/**
 * URL Parameters Utility Functions
 */

export const encodeSearchParams = (searchData) => {
  const params = new URLSearchParams();

  // display labels
  if (searchData.pickupLocation) params.set("pickup", searchData.pickupLocation);
  if (searchData.dropoffLocation) params.set("dropoff", searchData.dropoffLocation);

  // IDs (canonical)
  if (searchData.pickupCode) params.set("pickupCode", searchData.pickupCode);
  if (searchData.dropoffCode) params.set("dropoffCode", searchData.dropoffCode);

  // legacy support (only if canonical missing)
  if (!searchData.pickupCode && searchData.pickupGDSCode) params.set("pickupCode", searchData.pickupGDSCode);
  if (!searchData.dropoffCode && searchData.dropoffGDSCode) params.set("dropoffCode", searchData.dropoffGDSCode);

  // dates/times
  if (searchData.pickupDate) params.set("pickupDate", searchData.pickupDate);
  if (searchData.pickupTime) params.set("pickupTime", searchData.pickupTime);
  if (searchData.dropoffDate) params.set("dropoffDate", searchData.dropoffDate);
  if (searchData.dropoffTime) params.set("dropoffTime", searchData.dropoffTime);

  // meta
  if (searchData.sameLocation !== undefined) params.set("sameLocation", String(searchData.sameLocation));
  if (searchData.promoCode) params.set("promo", searchData.promoCode);
  if (searchData.searchId) params.set("search_id", String(searchData.searchId));
  if (searchData.rateId) params.set("rateId", searchData.rateId);

  // driver
  if (searchData.driverCountry) params.set("country", searchData.driverCountry);
  if (searchData.driverAge) params.set("age", searchData.driverAge);

  return params.toString();
};

export const decodeSearchParams = (searchParams) => {
  const params = new URLSearchParams(searchParams);

  const pickupLocation = params.get("pickup") || "";
  const dropoffLocation = params.get("dropoff") || "";

  // Canonical IDs
  const pickupCode = params.get("pickupCode") || params.get("pickup_location_id") || "";
  const dropoffCode = params.get("dropoffCode") || params.get("dropoff_location_id") || "";

  const pickupDate = params.get("pickupDate") || "";
  const pickupTime = params.get("pickupTime") || "10:00";
  const dropoffDate = params.get("dropoffDate") || "";
  const dropoffTime = params.get("dropoffTime") || "10:00";

  const sameLocationRaw = params.get("sameLocation");
  const sameLocation = sameLocationRaw === null ? undefined : sameLocationRaw === "true";

  const promoCode = params.get("promo") || "";
  const searchId = params.get("search_id") || "";
  const rateId = params.get("rateId") || "";

  const driverCountry = params.get("country") || "US";
  const driverAge = params.get("age") || "35";

  return {
    // labels
    pickupLocation,
    dropoffLocation,

    // canonical ids
    pickupCode,
    dropoffCode,

    // legacy aliases (so older code paths don’t break)
    pickupGDSCode: pickupCode,
    dropoffGDSCode: dropoffCode,
    pickup_location_id: pickupCode,
    dropoff_location_id: dropoffCode,

    // dates/times
    pickupDate,
    pickupTime,
    dropoffDate,
    dropoffTime,

    sameLocation,
    promoCode,
    searchId,
    rateId,

    driverCountry,
    driverAge,
  };
};

export const encodeCarParams = (carInfo) => {
  const params = new URLSearchParams();

  if (carInfo.vendor) params.set("vendor", carInfo.vendor);
  if (carInfo.carClass) params.set("carClass", carInfo.carClass);
  if (carInfo.pickupLocation) params.set("pickup", carInfo.pickupLocation);
  if (carInfo.dropoffLocation) params.set("dropoff", carInfo.dropoffLocation);
  if (carInfo.pickupDate) params.set("pickupDate", carInfo.pickupDate);
  if (carInfo.dropoffDate) params.set("dropoffDate", carInfo.dropoffDate);
  if (carInfo.pickupTime) params.set("pickupTime", carInfo.pickupTime);
  if (carInfo.dropoffTime) params.set("dropoffTime", carInfo.dropoffTime);
  if (carInfo.rateCode) params.set("rateCode", carInfo.rateCode);
  if (carInfo.rateId) params.set("rateId", carInfo.rateId);
  if (carInfo.searchId) params.set("search_id", carInfo.searchId);
  if (carInfo.carOfferHash) params.set("carOfferHash", carInfo.carOfferHash);

  params.set("_nav", Date.now().toString());
  return params.toString();
};

export const decodeCarParams = (searchParams) => {
  const params = new URLSearchParams(searchParams);

  return {
    vendor: params.get("vendor") || "",
    carClass: params.get("carClass") || "",
    pickupLocation: params.get("pickup") || "",
    dropoffLocation: params.get("dropoff") || "",
    pickupDate: params.get("pickupDate") || "",
    dropoffDate: params.get("dropoffDate") || "",
    pickupTime: params.get("pickupTime") || "",
    dropoffTime: params.get("dropoffTime") || "",
    rateCode: params.get("rateCode") || "",
    rateId: params.get("rateId") || "",
    searchId: params.get("searchId" || "")
  };
};

export const updateSearchURL = (navigate, searchData) => {
  const params = encodeSearchParams(searchData);
  const resultsUrl = `/results?${params}`;

  sessionStorage.setItem("originalSearchURL", resultsUrl);
  navigate(resultsUrl, { replace: false });
};

export const updateCarURL = (
  navigate,
  carInfo,
  openInNewTab = false,
  currentCurrency = null,
) => {
  const params = encodeCarParams(carInfo);
  const checkoutUrl = `/checkout?${params}`;

  const currentResultsURL = window.location.pathname + window.location.search;
  sessionStorage.setItem("originalSearchURL", currentResultsURL);

  // Save currency used for current search_id
  if (currentCurrency) {
    sessionStorage.setItem("search_id_currency", currentCurrency);
  }

  if (openInNewTab) {
    window.open(checkoutUrl, "_blank");
  } else {
    navigate(checkoutUrl, { replace: true });
  }
};

export const encodeVoucherParams = ({ emailId, reservationNumber }) => {
  const params = new URLSearchParams();

  if (emailId) params.set("email", emailId);
  if (reservationNumber) params.set("reservation", reservationNumber);

  // optional: prevent cached navigation issues (same pattern as encodeCarParams)
  params.set("_nav", Date.now().toString());

  return params.toString();
};

export const decodeVoucherParams = (searchParams) => {
  const params = new URLSearchParams(searchParams);

  return {
    emailId: params.get("email") || "",
    reservationNumber: params.get("reservation") || "",
  };
};

export const updateVoucherURL = (
  navigate,
  { emailId, reservationNumber },
  openInNewTab = false
) => {
  const params = encodeVoucherParams({ emailId, reservationNumber });
  const voucherUrl = `/rental-voucher?${params}`;

  if (openInNewTab) {
    window.open(voucherUrl, "_blank");
  } else {
    navigate(voucherUrl, { replace: false });
  }
};

/**
 * Navigate to results page with a clean search_id URL
 * Called after registerSearch returns a search_id
 * @param {Function} navigate - React Router navigate function
 * @param {string} searchId - The search_id returned by registerSearch
 */
export const navigateToSearchResults = (navigate, searchId) => {
  const params = new URLSearchParams();
  params.set("search_id", searchId);
  const resultsUrl = `/results?${params.toString()}`;
  sessionStorage.setItem("originalSearchURL", resultsUrl);
  navigate(resultsUrl, { replace: false });
};
